import React, { useState } from 'react'
import { 
  Layout, 
  Row, 
  Col, 
  Card, 
  Input, 
  Select, 
  Button, 
  Typography, 
  Space, 
  Pagination,
  Spin,
  Empty,
  Breadcrumb,
  Tag,
  Avatar,
  List
} from 'antd'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { 
  SearchOutlined, 
  CalendarOutlined, 
  UserOutlined, 
  EyeOutlined,
  HomeOutlined,
  ReadOutlined,
  ClockCircleOutlined
} from '@ant-design/icons'
import { Helmet } from 'react-helmet-async'
import { useQuery } from '@tanstack/react-query'
import { articlesApi, siteSettingsApi } from '../../services/api'

const { Content } = Layout
const { Title, Text, Paragraph } = Typography
const { Option } = Select

const BlogPage: React.FC = () => {
  const { i18n, t } = useTranslation()
  const currentLang = i18n.language as 'en' | 'zh'
  const [searchParams, setSearchParams] = useSearchParams()
  
  // State for filters
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [category, setCategory] = useState(searchParams.get('category') || '')
  const [tag, setTag] = useState(searchParams.get('tag') || '')
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'publishedAt')
  const [order, setOrder] = useState(searchParams.get('order') || 'desc')
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'))

  // Fetch site settings for dynamic content
  const { data: siteSettingsData } = useQuery({
    queryKey: ['publicSiteSettings'],
    queryFn: siteSettingsApi.getPublicSettings,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  // Fetch articles with filters
  const { data: articlesData, isLoading, error } = useQuery({
    queryKey: ['articles', search, category, tag, sortBy, order, currentPage],
    queryFn: () => articlesApi.getAll({
      search,
      category,
      tag,
      sortBy,
      order,
      page: currentPage,
      limit: 12
    })
  })

  // Fetch featured articles
  const { data: featuredData } = useQuery({
    queryKey: ['featured-articles'],
    queryFn: () => articlesApi.getAll({ featured: true, limit: 5 })
  })

  // Get all articles to extract categories and tags
  const { data: allArticlesData } = useQuery({
    queryKey: ['allArticles'],
    queryFn: () => articlesApi.getAll({ limit: 1000 }), // Get all articles
    staleTime: 1000 * 60 * 10, // 10 minutes cache
  })

  const settings = siteSettingsData?.settings

  // Helper function to get localized text
  const getLocalizedText = (text: { en: string; zh: string } | undefined, fallback: string = '') => {
    if (!text) return fallback
    return text[currentLang] || text.en || fallback
  }

  // Extract unique categories from articles
  const getUniqueCategories = () => {
    if (!allArticlesData?.articles) return []
    const categories = new Set<string>()
    allArticlesData.articles.forEach((article: any) => {
      if (article.category?.en) categories.add(article.category.en)
      if (article.category?.zh && currentLang === 'zh') categories.add(article.category.zh)
    })
    return Array.from(categories).sort()
  }

  // Extract unique tags from articles
  const getUniqueTags = () => {
    if (!allArticlesData?.articles) return []
    const tags = new Set<string>()
    allArticlesData.articles.forEach((article: any) => {
      if (article.tags) {
        article.tags.forEach((tag: any) => {
          if (typeof tag === 'string') {
            tags.add(tag)
          } else if (tag?.en) {
            tags.add(tag.en)
            if (tag.zh && currentLang === 'zh') tags.add(tag.zh)
          }
        })
      }
    })
    return Array.from(tags).sort()
  }

  const handleSearch = (value: string) => {
    setSearch(value)
    setCurrentPage(1)
    updateSearchParams({ search: value, page: '1' })
  }

  const handleCategoryChange = (value: string) => {
    setCategory(value)
    setCurrentPage(1)
    updateSearchParams({ category: value, page: '1' })
  }

  const handleTagChange = (value: string) => {
    setTag(value)
    setCurrentPage(1)
    updateSearchParams({ tag: value, page: '1' })
  }

  const handleSortChange = (value: string) => {
    setSortBy(value)
    setCurrentPage(1)
    updateSearchParams({ sortBy: value, page: '1' })
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    updateSearchParams({ page: page.toString() })
  }

  const updateSearchParams = (newParams: Record<string, string>) => {
    const params = new URLSearchParams(searchParams)
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        params.set(key, value)
      } else {
        params.delete(key)
      }
    })
    setSearchParams(params)
  }

  const clearFilters = () => {
    setSearch('')
    setCategory('')
    setTag('')
    setSortBy('publishedAt')
    setOrder('desc')
    setCurrentPage(1)
    setSearchParams({})
  }

  const categories = getUniqueCategories()
  const commonTags = getUniqueTags()

  const formatDate = (dateString: string) => {
    const locale = i18n.language.startsWith('zh') ? 'zh-HK' : 'en-US'
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getReadingTime = (content: string) => {
    // Estimate reading time based on content length
    const words = content?.split(' ').length || 0
    const minutes = Math.ceil(words / 200)
    return t('blog.meta.readingTime', { minutes })
  }

  return (
    <>
      <Helmet>
        <title>{t('navigation.blog')} - {getLocalizedText(settings?.siteName, 'MedCareHub')}</title>
        <meta name="description" content={getLocalizedText(settings?.pages?.blog?.description, t('blog.subtitle'))} />
        <meta name="keywords" content={`blog, articles, health, ${getLocalizedText(settings?.seo?.keywords, 'medical, wellness, tips')}`} />
      </Helmet>

      <Content>
        {/* Breadcrumb */}
        <div className="bg-gray-50 py-4">
          <div className="max-w-6xl mx-auto px-4">
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to="/">
                  <HomeOutlined /> {t('navigation.home')}
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t('navigation.blog')}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
        </div>

        {/* Banner Section */}
        <section 
          className="h-[40vh] text-white relative bg-center bg-cover flex items-center justify-center"
          style={{
            backgroundImage: `url('${settings?.pages?.blog?.bannerImage || '/blog-banner.jpg'}')`,
            backgroundColor: '#1890ff' // Fallback color
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          <div className="max-w-6xl mx-auto px-4 text-center relative z-10">
            <Title level={1} className="text-4xl font-bold text-white mb-4 drop-shadow-lg" style={{ color: '#ffffff' }}>
              {getLocalizedText(settings?.pages?.blog?.title, t('blog.title'))}
            </Title>
            <Paragraph className="text-xl text-white max-w-3xl mx-auto drop-shadow-lg">
              {getLocalizedText(settings?.pages?.blog?.subtitle, t('blog.subtitle'))}
            </Paragraph>
          </div>
        </section>

        {/* Header */}
        <section className="py-12 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-8">
              <Title level={2} className="text-3xl font-bold text-gray-900 mb-4">
                {t('blog.filters.title', 'Find Articles')}
              </Title>
            </div>

            {/* Search and Filters */}
            <Row gutter={[16, 16]} className="mb-8">
              <Col xs={24} md={6}>
                <Input.Search
                  placeholder={t('common.search') + ' ' + t('navigation.blog') + '...'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onSearch={handleSearch}
                  size="large"
                  prefix={<SearchOutlined />}
                />
              </Col>
              <Col xs={24} md={4}>
                <Select
                  placeholder={t('blog.meta.category')}
                  value={category || undefined}
                  onChange={handleCategoryChange}
                  size="large"
                  style={{ width: '100%' }}
                  allowClear
                >
                  {categories.map(cat => (
                    <Option key={cat} value={cat}>{cat}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} md={4}>
                <Select
                  placeholder={t('blog.meta.tags')}
                  value={tag || undefined}
                  onChange={handleTagChange}
                  size="large"
                  style={{ width: '100%' }}
                  allowClear
                >
                  {commonTags.map(t => (
                    <Option key={t} value={t}>{t}</Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} md={4}>
                <Select
                  value={sortBy}
                  onChange={handleSortChange}
                  size="large"
                  style={{ width: '100%' }}
                >
                  <Option value="publishedAt">{t('products.filters.sortOptions.newest')}</Option>
                  <Option value="title">{t('blog.title')}</Option>
                  <Option value="views">{t('products.filters.sortOptions.popular')}</Option>
                </Select>
              </Col>
              <Col xs={24} md={3}>
                <Button 
                  onClick={clearFilters}
                  size="large"
                  style={{ width: '100%' }}
                >
                  {t('common.clear')}
                </Button>
              </Col>
            </Row>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 py-8">
          <Row gutter={[32, 32]}>
            {/* Main Content */}
            <Col xs={24} lg={16}>
              {isLoading ? (
                <div className="text-center py-20">
                  <Spin size="large" />
                  <div className="mt-4 text-gray-500">{t('common.loading')}</div>
                </div>
              ) : error ? (
                <div className="text-center py-20">
                  <Empty description={t('messages.error.serverError')} />
                </div>
              ) : !articlesData?.articles?.length ? (
                <div className="text-center py-20">
                  <Empty description={t('messages.error.notFound')} />
                </div>
              ) : (
                <>
                  {/* Results count */}
                  <div className="mb-6 flex justify-between items-center">
                    <Text className="text-gray-600">
                      {t('products.messages.showingRange', { from: ((currentPage - 1) * 12) + 1, to: Math.min(currentPage * 12, articlesData.pagination?.total || articlesData.articles.length), total: articlesData.pagination?.total || articlesData.articles.length })}
                    </Text>
                  </div>

                  {/* Articles List */}
                  <div className="space-y-6">
                    {articlesData.articles.map((article: any) => (
                      <Card key={article._id} hoverable className="overflow-hidden">
                        <Row gutter={24}>
                          <Col xs={24} sm={8}>
                            <div className="h-48 bg-white flex items-center justify-center p-4">
                              {(() => {
                                const localized = article.featuredImage?.[currentLang]?.url;
                                const legacy = article.featuredImage?.url;
                                const src = localized || legacy;
                                const alt = article.featuredImage?.[currentLang]?.alt || article.featuredImage?.alt?.[currentLang] || article.title?.[currentLang] || article.title?.en;
                                if (src) {
                                  return (
                                    <img 
                                      src={src}
                                      alt={alt}
                                      className="max-w-full max-h-full object-contain rounded my-2"
                                    />
                                  );
                                }
                                return (
                                <ReadOutlined className="text-4xl text-gray-400" />
                                );
                              })()}
                            </div>
                          </Col>
                          <Col xs={24} sm={16}>
                            <div className="h-full flex flex-col justify-between">
                              <div>
                                <Link to={`/blog/${article._id}`}>
                                  <Title level={3} className="mb-2 hover:text-blue-600 transition-colors">
                                    {getLocalizedText(article.title, 'Article Title')}
                                  </Title>
                                </Link>
                                
                                <Paragraph className="text-gray-600 mb-3" ellipsis={{ rows: 2 }}>
                                  {getLocalizedText(article.excerpt, 'Article excerpt')}
                                </Paragraph>

                                {/* Tags */}
                                <div className="mb-4">
                                  {article.tags?.slice(0, 3).map((tag: any, index: number) => (
                                    <Tag key={index} color="blue" className="mb-1">
                                      {typeof tag === 'string' ? tag : getLocalizedText(tag, 'Tag')}
                                    </Tag>
                                  ))}
                                </div>
                              </div>

                              {/* Meta info */}
                              <div className="flex justify-between items-center text-sm text-gray-500">
                                <Space>
                                  <CalendarOutlined />
                                  <span>{formatDate(article.publishedAt)}</span>
                                  <UserOutlined />
                                  <span>{t('blog.meta.author', { author: article.author?.name || 'Admin' })}</span>
                                </Space>
                                <Space>
                                  <EyeOutlined />
                                  <span>{t('blog.meta.views', { count: article.viewCount || 0 })}</span>
                                  <ClockCircleOutlined />
                                  <span>{getReadingTime(getLocalizedText(article.content, ''))}</span>
                                </Space>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </Card>
                    ))}
                  </div>

                  {/* Pagination */}
                  <div className="text-center mt-12">
                    <Pagination
                      current={currentPage}
                      total={articlesData.pagination?.total || articlesData.articles.length}
                      pageSize={12}
                      onChange={handlePageChange}
                      showSizeChanger={false}
                      showQuickJumper
                      showTotal={(total, range) => t('products.messages.paginationTotal', { from: range[0], to: range[1], total })}
                    />
                  </div>
                </>
              )}
            </Col>

            {/* Sidebar */}
            <Col xs={24} lg={8}>
              <Space direction="vertical" size="large" className="w-full">
                {/* Featured Articles */}
                {featuredData?.articles?.length > 0 && (
                  <Card title={t('blog.actions.relatedArticles')}>
                    <List
                      dataSource={featuredData.articles.slice(0, 5)}
                      renderItem={(article: any) => (
                        <List.Item key={article._id}>
                          <List.Item.Meta
                            avatar={
                              <Avatar 
                                shape="square" 
                                size={64}
                                src={article.featuredImage?.url}
                                icon={<ReadOutlined />}
                              />
                            }
                            title={
                              <Link to={`/blog/${article._id}`} className="text-sm font-medium">
                                {getLocalizedText(article.title, 'Featured Article')}
                              </Link>
                            }
                            description={
                              <Text className="text-xs text-gray-500">
                                {formatDate(article.publishedAt)}
                              </Text>
                            }
                          />
                        </List.Item>
                      )}
                    />
                  </Card>
                )}

                {/* Popular Tags */}
                {commonTags.length > 0 && (
                  <Card title={t('blog.meta.tags')}>
                    <div className="space-y-2">
                      {commonTags.slice(0, 15).map(tagItem => (
                        <Tag 
                          key={tagItem}
                          color={tag === tagItem ? 'blue' : 'default'}
                          className="cursor-pointer mb-2"
                          onClick={() => handleTagChange(tagItem)}
                        >
                          {tagItem}
                        </Tag>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Categories */}
                {categories.length > 0 && (
                  <Card title={t('blog.meta.category')}>
                    <div className="space-y-2">
                      {categories.map(cat => (
                        <div 
                          key={cat}
                          className={`p-2 rounded cursor-pointer hover:bg-gray-50 ${category === cat ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
                          onClick={() => handleCategoryChange(cat)}
                        >
                          {cat}
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </Space>
            </Col>
          </Row>
        </div>
      </Content>
    </>
  )
}

export default BlogPage
